import {
	type AdminGoldenAsset,
	type AdminSeriesDetail,
	type CreateCharacterInput,
	type CreateLocationInput,
	type GenerationFailureReason,
	type GenerationJob,
	GenerationJobStatus,
	GenerationKind,
	type GenerationTargetType,
	type GoldenAssetKind,
	GoldenAssetOwnerType,
	type UpdateCharacterInput,
	type UpdateLocationInput,
	type UpdateSeriesInput,
	type UpsertSeriesBibleInput,
} from "@storyline/shared";
import { useCallback, useEffect, useRef, useState } from "react";
import { createGeneration, listSeriesGenerations, refreshGeneration, retryGeneration } from "@/src/common/services/generation-http.service";
import { approveAsset, listAssets, lockAsset, unlockAsset } from "@/src/series/services/assets-http.service";
import {
	createCharacter,
	createLocation,
	deleteCharacter,
	deleteLocation,
	deleteSeries,
	getSeries,
	setSeriesBibleLock,
	updateCharacter,
	updateLocation,
	updateSeries,
	uploadSeriesCover,
	upsertSeriesBible,
} from "@/src/series/services/series-http.service";

const POLL_INTERVAL_MS = 3000;

const isActive = (job: GenerationJob): boolean =>
	job.status === GenerationJobStatus.PENDING || job.status === GenerationJobStatus.PROCESSING;

export const useSeriesDashboard = (seriesId: string) => {
	const [detail, setDetail] = useState<AdminSeriesDetail | null>(null);
	const [assets, setAssets] = useState<AdminGoldenAsset[]>([]);
	const [generations, setGenerations] = useState<GenerationJob[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [hasError, setHasError] = useState(false);
	const pollingRef = useRef(false);

	const reload = useCallback(async () => {
		try {
			const [nextDetail, nextAssets, nextGenerations] = await Promise.all([
				getSeries(seriesId),
				listAssets(seriesId),
				listSeriesGenerations(seriesId),
			]);
			setDetail(nextDetail);
			setAssets(nextAssets.assets);
			setGenerations(nextGenerations.jobs);
			setHasError(false);
		} catch {
			setHasError(true);
		} finally {
			setIsLoading(false);
		}
	}, [seriesId]);

	useEffect(() => {
		setIsLoading(true);
		void reload();
	}, [reload]);

	const activeJobs = generations.filter(isActive);

	useEffect(() => {
		if (activeJobs.length === 0) {
			return;
		}
		const ids = activeJobs.map((job) => job.id);
		const timer = setInterval(async () => {
			if (pollingRef.current) {
				return;
			}
			pollingRef.current = true;
			try {
				await Promise.all(ids.map((id) => refreshGeneration(id).catch(() => null)));
				await reload();
			} finally {
				pollingRef.current = false;
			}
		}, POLL_INTERVAL_MS);
		return () => clearInterval(timer);
	}, [activeJobs, reload]);

	const generateAsset = useCallback(
		async (targetType: GenerationTargetType, targetId: string, assetKind: GoldenAssetKind) => {
			await createGeneration({ kind: GenerationKind.GOLDEN_ASSET, seriesId, targetType, targetId, assetKind });
			await reload();
		},
		[seriesId, reload],
	);

	const generateCover = useCallback(async () => {
		await createGeneration({ kind: GenerationKind.SERIES_COVER, seriesId, targetType: GoldenAssetOwnerType.SERIES, targetId: seriesId });
		await reload();
	}, [seriesId, reload]);

	const approve = useCallback(
		async (assetId: string) => {
			await approveAsset(assetId);
			await reload();
		},
		[reload],
	);

	const lock = useCallback(
		async (assetId: string) => {
			await lockAsset(assetId);
			await reload();
		},
		[reload],
	);

	const unlock = useCallback(
		async (assetId: string) => {
			await unlockAsset(assetId);
			await reload();
		},
		[reload],
	);

	const retry = useCallback(
		async (jobId: string, failureReason: GenerationFailureReason) => {
			await retryGeneration(jobId, { failureReason });
			await reload();
		},
		[reload],
	);

	const saveBible = useCallback(
		async (body: UpsertSeriesBibleInput) => {
			await upsertSeriesBible(seriesId, body);
			await reload();
		},
		[seriesId, reload],
	);

	const toggleBibleLock = useCallback(
		async (locked: boolean) => {
			await setSeriesBibleLock(seriesId, locked);
			await reload();
		},
		[seriesId, reload],
	);

	const addCharacter = useCallback(
		async (body: CreateCharacterInput) => {
			await createCharacter(seriesId, body);
			await reload();
		},
		[seriesId, reload],
	);

	const editCharacter = useCallback(
		async (characterId: string, body: UpdateCharacterInput) => {
			await updateCharacter(characterId, body);
			await reload();
		},
		[reload],
	);

	const removeCharacter = useCallback(
		async (characterId: string) => {
			await deleteCharacter(characterId);
			await reload();
		},
		[reload],
	);

	const addLocation = useCallback(
		async (body: CreateLocationInput) => {
			await createLocation(seriesId, body);
			await reload();
		},
		[seriesId, reload],
	);

	const editLocation = useCallback(
		async (locationId: string, body: UpdateLocationInput) => {
			await updateLocation(locationId, body);
			await reload();
		},
		[reload],
	);

	const removeLocation = useCallback(
		async (locationId: string) => {
			await deleteLocation(locationId);
			await reload();
		},
		[reload],
	);

	const uploadCover = useCallback(
		async (file: File) => {
			await uploadSeriesCover(seriesId, file);
			await reload();
		},
		[seriesId, reload],
	);

	const saveMetadata = useCallback(
		async (body: UpdateSeriesInput) => {
			await updateSeries(seriesId, body);
			await reload();
		},
		[seriesId, reload],
	);

	const removeSeries = useCallback(() => deleteSeries(seriesId), [seriesId]);

	return {
		detail,
		assets,
		generations,
		isLoading,
		hasError,
		reload,
		generateAsset,
		generateCover,
		approve,
		lock,
		unlock,
		retry,
		saveBible,
		toggleBibleLock,
		addCharacter,
		editCharacter,
		removeCharacter,
		addLocation,
		editLocation,
		removeLocation,
		uploadCover,
		saveMetadata,
		removeSeries,
	};
};
